import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';
import { FeatureEvaluationContext } from './context';
import {
  buildFeatureStateMap,
  isFeatureEnabled,
  type FeatureEvaluationOptions,
} from './evaluation';
import { getDefaultFeatureOverrides } from './defaults';
import {
  evaluateFeatureAccess,
  FeatureAccessResult,
  isFeatureAccessAllowed,
} from './access';
import { FeatureKey } from './types';

type FeatureStateMap = Readonly<Record<FeatureKey, boolean>>;

const FeatureFlagsContext = createContext<FeatureStateMap | null>(null);

export interface FeatureFlagsProviderProps {
  children: ReactNode;
  states?: FeatureStateMap;
  options?: FeatureEvaluationOptions;
  /** Future: pass subject/platform context for contextual evaluation. */
  evaluationContext?: FeatureEvaluationContext;
}

export function FeatureFlagsProvider({
  children,
  states,
  options,
  evaluationContext,
}: FeatureFlagsProviderProps) {
  const value = useMemo(() => {
    if (states) {
      return states;
    }

    return buildFeatureStateMap({
      ...options,
      evaluationContext,
      overrides: {
        ...getDefaultFeatureOverrides(),
        ...(options?.overrides ?? {}),
      },
    });
  }, [states, options, evaluationContext]);

  return (
    <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>
  );
}

function resolveFeatureState(
  key: FeatureKey,
  contextStates: FeatureStateMap | null,
  options: FeatureEvaluationOptions,
): boolean {
  if (contextStates) {
    return contextStates[key];
  }

  return isFeatureEnabled(key, {
    ...options,
    overrides: {
      ...getDefaultFeatureOverrides(),
      ...(options.overrides ?? {}),
    },
  });
}

export function useFeature(
  key: FeatureKey,
  options: FeatureEvaluationOptions = {},
): boolean {
  const contextStates = useContext(FeatureFlagsContext);

  return useMemo(
    () => resolveFeatureState(key, contextStates, options),
    [key, contextStates, options],
  );
}

export function useFeatures(
  options: FeatureEvaluationOptions = {},
): FeatureStateMap {
  const contextStates = useContext(FeatureFlagsContext);

  return useMemo(() => {
    if (contextStates) {
      return contextStates;
    }

    return buildFeatureStateMap({
      ...options,
      overrides: {
        ...getDefaultFeatureOverrides(),
        ...(options.overrides ?? {}),
      },
    });
  }, [contextStates, options]);
}

export function useFeatureAccess(
  key: FeatureKey,
  hasPermission: boolean,
  options: FeatureEvaluationOptions = {},
): FeatureAccessResult {
  const enabled = useFeature(key, options);

  return useMemo(
    () => evaluateFeatureAccess(enabled, hasPermission),
    [enabled, hasPermission],
  );
}

export function useFeatureAccessAllowed(
  key: FeatureKey,
  hasPermission: boolean,
  options: FeatureEvaluationOptions = {},
): boolean {
  const enabled = useFeature(key, options);

  return useMemo(
    () => isFeatureAccessAllowed(enabled, hasPermission),
    [enabled, hasPermission],
  );
}

export interface FeatureGateProps {
  feature: FeatureKey;
  children: ReactNode;
  fallback?: ReactNode;
}

export function FeatureGate({ feature, children, fallback = null }: FeatureGateProps) {
  const enabled = useFeature(feature);
  return enabled ? <>{children}</> : <>{fallback}</>;
}

export interface FeatureAccessGateProps {
  feature: FeatureKey;
  hasPermission: boolean;
  children: ReactNode;
  hiddenFallback?: ReactNode;
  deniedFallback?: ReactNode;
}

export function FeatureAccessGate({
  feature,
  hasPermission,
  children,
  hiddenFallback = null,
  deniedFallback = null,
}: FeatureAccessGateProps) {
  const access = useFeatureAccess(feature, hasPermission);

  if (access === FeatureAccessResult.HIDDEN) {
    return <>{hiddenFallback}</>;
  }

  if (access === FeatureAccessResult.DENY) {
    return <>{deniedFallback}</>;
  }

  return <>{children}</>;
}

export type { FeatureEvaluationContext, FeatureClientPlatform, FeatureSubjectContext } from './context';
export { FeatureAccessResult };
